import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getOppFundingHedgeScreen,
  getOppOrderbookExchanges,
  type OppFundingHedgeScreenItem,
  type OppFundingHedgeScreenResponse,
  type OppFundingHedgeScreenSortBy,
} from '@/services/oppDetectorApi';
import '../OppTop/OppTop.css';

const DEFAULT_LIMIT = 100;
const POLL_MS = 30_000;
const DEFAULT_HISTORY_WINDOW_SECS = 86_400;

const SORT_OPTIONS: Array<{ value: OppFundingHedgeScreenSortBy; label: string }> = [
  { value: 'abs_next_net_desc', label: '下次净费绝对值 ↓' },
  { value: 'abs_next_net_asc', label: '下次净费绝对值 ↑' },
  { value: 'next_net_desc', label: '下次净费 ↓' },
  { value: 'next_net_asc', label: '下次净费 ↑' },
  { value: 'next_time_asc', label: '距下次结算 ↑' },
  { value: 'abs_hist_net_desc', label: '24h 净费绝对值 ↓' },
];

function formatFundingRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(4)}%`;
}

function formatApy(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(2)}%`;
}

function formatDirection(direction: string): string {
  return direction === 'long1_short2' ? 'L1S2' : 'S1L2';
}

function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatSettlementKind(
  kind: OppFundingHedgeScreenItem['upcoming']['next_settlement_kind'],
  exchange1: string,
  exchange2: string,
): string {
  switch (kind) {
    case 'both':
      return '两所';
    case 'leg1_only':
      return exchange1;
    case 'leg2_only':
      return exchange2;
    default:
      return kind;
  }
}

function fundingClass(value: number): string {
  if (!Number.isFinite(value) || Math.abs(value) < 1e-12) return '';
  return value > 0 ? 'opp-funding-positive' : 'opp-funding-negative';
}

function toUiExchange(exchange: string): string {
  const normalized = exchange.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'aster') return 'Aster';
  if (normalized === 'edgex') return 'EdgeX';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return exchange;
}

function emptyPage(): OppFundingHedgeScreenResponse {
  return {
    exchanges: [],
    exchange_pair_count: 0,
    now_ms: 0,
    history_window_secs: DEFAULT_HISTORY_WINDOW_SECS,
    history_start_ms: 0,
    history_end_ms: 0,
    total_pairs: 0,
    count: 0,
    items: [],
  };
}

export function OppFundingHedge() {
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<OppFundingHedgeScreenSortBy>('abs_next_net_desc');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<OppFundingHedgeScreenResponse>(() => emptyPage());
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void getOppOrderbookExchanges()
      .then((list) => {
        if (!mounted.current) return;
        setExchanges(list);
        setSelectedExchanges((prev) => {
          if (prev.length >= 2) {
            return prev.filter((exchange) => list.includes(exchange));
          }
          return list.slice(0, Math.min(2, list.length));
        });
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const toggleExchange = useCallback((exchange: string) => {
    setSelectedExchanges((prev) => {
      if (prev.includes(exchange)) {
        return prev.filter((item) => item !== exchange);
      }
      return [...prev, exchange];
    });
    setOffset(0);
  }, []);

  const load = useCallback(async () => {
    if (selectedExchanges.length < 2) {
      setPage(emptyPage());
      setLoading(false);
      return;
    }

    try {
      const data = await getOppFundingHedgeScreen({
        exchanges: selectedExchanges,
        history_window_secs: DEFAULT_HISTORY_WINDOW_SECS,
        sort_by: sortBy,
        limit,
        offset,
      });
      if (!mounted.current) return;
      setPage(data ?? emptyPage());
      setError(data ? null : '暂无即将结算的 funding 数据');
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [limit, offset, selectedExchanges, sortBy]);

  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    void load();
    const timer = autoRefresh ? window.setInterval(() => void load(), POLL_MS) : null;
    return () => {
      mounted.current = false;
      if (timer != null) window.clearInterval(timer);
    };
  }, [autoRefresh, load]);

  const resetOffset = useCallback(() => setOffset(0), []);

  const rows = page.items;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(page.total_pairs / Math.max(limit, 1))),
    [limit, page.total_pairs],
  );
  const currentPage = useMemo(
    () => Math.floor(offset / Math.max(limit, 1)) + 1,
    [limit, offset],
  );

  const canQuery = selectedExchanges.length >= 2;
  const historyHours = Math.round(page.history_window_secs / 3600);

  const jumpToOppSpreadLive = useCallback((item: OppFundingHedgeScreenItem) => {
    const params = new URLSearchParams({
      symbol: item.symbol,
      exchange1: toUiExchange(item.exchange1),
      exchange2: toUiExchange(item.exchange2),
      instId1: item.inst_id1,
      instId2: item.inst_id2,
      autoQuery: '1',
    });
    const url = `${window.location.origin}/trading?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <div className="opp-top-page">
      <header className="opp-top-header">
        {/* <h2>资金费套利</h2> */}
        <p className="opp-top-desc">
          {/* 数据来自 opp-detector <code>/api/opp-detector/funding/pair-hedge-screen</code>。 */}
          选择多个交易所后，对所有两两组合计算 funding 并统一排序。
          <strong>主列</strong>为下一次结算节点的对冲净资金费；
          <strong>辅列</strong>为过去 {historyHours || 24}h 历史 settlement 统计。
          {autoRefresh ? `每 ${POLL_MS / 1000}s 自动刷新` : '自动刷新已停止'}。
        </p>
      </header>

      <div className="opp-top-toolbar">
        <button type="button" disabled={loading || !canQuery} onClick={() => void load()}>
          立即刷新
        </button>
        <button type="button" onClick={() => setAutoRefresh((v) => !v)}>
          {autoRefresh ? '停止自动刷新' : '启动自动刷新'}
        </button>
        <div className="opp-funding-exchange-checkboxes">
          <span className="opp-funding-exchange-checkboxes-label">交易所（至少 2 个）</span>
          <div className="opp-funding-exchange-checkboxes-list">
            {exchanges.map((exchange) => (
              <label key={exchange} className="opp-funding-exchange-checkbox">
                <input
                  type="checkbox"
                  checked={selectedExchanges.includes(exchange)}
                  onChange={() => toggleExchange(exchange)}
                />
                <span>{exchange}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="opp-top-filter-field">
          <span>排序</span>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as OppFundingHedgeScreenSortBy);
              resetOffset();
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>每页条数</span>
          <input
            type="number"
            min={1}
            step={1}
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value);
              setLimit(Number.isFinite(next) && next > 0 ? Math.floor(next) : DEFAULT_LIMIT);
              resetOffset();
            }}
          />
        </label>
        <button
          type="button"
          disabled={loading || offset <= 0}
          onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
        >
          上一页
        </button>
        <button
          type="button"
          disabled={loading || offset + limit >= page.total_pairs}
          onClick={() => setOffset((prev) => prev + limit)}
        >
          下一页
        </button>
        <p className="opp-top-meta">
          已选 {selectedExchanges.length} 所，{page.exchange_pair_count || 0} 个组合；
          第 {currentPage} / {totalPages} 页，共 {page.total_pairs} 条
        </p>
        {page.history_start_ms > 0 && (
          <p className="opp-top-meta">
            历史窗口：{formatMs(page.history_start_ms)} — {formatMs(page.history_end_ms)}
          </p>
        )}
        {lastOkAt != null && (
          <p className="opp-top-meta">上次成功：{formatMs(lastOkAt)}</p>
        )}
      </div>

      {!canQuery && (
        <div className="opp-top-error">请至少选择两个交易所后再查询。</div>
      )}

      {error && <div className="opp-top-error">{error}</div>}

      <div className="opp-top-table-wrap">
        {rows.length === 0 && !loading && canQuery && !error ? (
          <div className="opp-top-empty">暂无数据</div>
        ) : (
          <table className="opp-top-table opp-funding-screen-table">
            <thead>
              <tr>
                <th rowSpan={2}>#</th>
                <th rowSpan={2}>交易所对</th>
                <th rowSpan={2}>Symbol</th>
                <th rowSpan={2}>Leg 1</th>
                <th rowSpan={2}>Leg 2</th>
                <th colSpan={5} className="opp-funding-section-head opp-funding-section-head--primary">
                  下次结算
                </th>
                <th colSpan={3} className="opp-funding-section-head opp-funding-section-head--secondary">
                  过去 {historyHours || 24}h
                </th>
              </tr>
              <tr>
                <th className="num opp-funding-section-head--primary">净资金费</th>
                <th className="num opp-funding-section-head--primary">倒计时</th>
                <th className="opp-funding-section-head--primary">范围</th>
                <th className="opp-funding-section-head--primary">方向</th>
                <th className="num opp-funding-section-head--primary">L1/S2 rate</th>
                <th className="num opp-funding-section-head--secondary">净资金费</th>
                <th className="num opp-funding-section-head--secondary">年化 APY</th>
                <th className="num opp-funding-section-head--secondary">方向</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const upcoming = row.upcoming;
                const historical = row.historical;
                const displayExchange1 = toUiExchange(row.exchange1);
                const displayExchange2 = toUiExchange(row.exchange2);
                return (
                  <tr
                    key={`${row.exchange1}-${row.exchange2}-${row.symbol}-${row.inst_id1}-${row.inst_id2}-${index}`}
                    className="opp-top-row-clickable"
                    onClick={() => jumpToOppSpreadLive(row)}
                    title="点击跳转到 Opp Spread/K 线并自动查询"
                  >
                    <td>{offset + index + 1}</td>
                    <td>
                      {displayExchange1} ↔ {displayExchange2}
                    </td>
                    <td>{row.symbol}</td>
                    <td>
                      <span className="opp-top-inst-cell" title={`${displayExchange1}: ${row.inst_id1}`}>
                        <span className="opp-top-inst-exchange">{displayExchange1}</span>
                        <span className="opp-top-inst-id">{row.inst_id1}</span>
                        <span className="opp-top-inst-id">{formatFundingRate(upcoming.leg1_rate)}</span>
                      </span>
                    </td>
                    <td>
                      <span className="opp-top-inst-cell" title={`${displayExchange2}: ${row.inst_id2}`}>
                        <span className="opp-top-inst-exchange">{displayExchange2}</span>
                        <span className="opp-top-inst-id">{row.inst_id2}</span>
                        <span className="opp-top-inst-id">{formatFundingRate(upcoming.leg2_rate)}</span>
                      </span>
                    </td>
                    <td className={`num ${fundingClass(upcoming.next_net_funding)}`}>
                      {formatFundingRate(upcoming.next_net_funding)}
                    </td>
                    <td className="num">{formatDuration(upcoming.ms_to_next_settlement)}</td>
                    <td>
                      {formatSettlementKind(
                        upcoming.next_settlement_kind,
                        displayExchange1,
                        displayExchange2,
                      )}
                    </td>
                    <td>{formatDirection(upcoming.direction)}</td>
                    <td className="num opp-funding-muted">
                      {formatFundingRate(upcoming.leg1_rate)} / {formatFundingRate(upcoming.leg2_rate)}
                    </td>
                    <td className={`num opp-funding-hist-col ${historical ? fundingClass(historical.net_funding_total) : ''}`}>
                      {historical ? formatFundingRate(historical.net_funding_total) : '—'}
                    </td>
                    <td className={`num opp-funding-hist-col ${historical ? fundingClass(historical.annualized_apy) : ''}`}>
                      {historical ? formatApy(historical.annualized_apy) : '—'}
                    </td>
                    <td className="opp-funding-hist-col">
                      {historical ? formatDirection(historical.direction) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
